import { computed, inject, Injectable, signal } from '@angular/core'
import { MAP } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import { API_URL, PERSONAL_LAYERS_ORDER } from '@core/constants/constant.data'
import { LAYERS_ARRAY } from '@core/constants/layers.data'
import { asyncError, asyncLoading, AsyncState, asyncSuccess } from '@core/models/async-state.model'
import { LayerPersonalPayload, LayerUploaded } from '@core/models/layers.model'
import { environment } from '@env/environment'
import { bbox } from '@turf/bbox'
import Keycloak from 'keycloak-js'
import L from 'leaflet'
import { GeojsonService } from './geojson.service'
import { LayersService } from './layers.service'
import { SLDService } from './sld.service'

//@Todo this service should only have logic for communication with api, or selecting and unselecting personal layers. But not both
@Injectable({
  providedIn: 'root',
})
export class UploadedLayersService extends HttpService {
  private readonly _map = inject(MAP)
  private readonly _geoJsonService = inject(GeojsonService)
  private readonly _sldService = inject(SLDService)
  private readonly _layerService = inject(LayersService)
  readonly keycloak = inject(Keycloak)
  protected _baseUrl = API_URL + '/upload'

  standardLayers$ = this._layerService.layers$.asReadonly()

  personalLayersState$ = signal<AsyncState<LayerUploaded[]>>(asyncLoading())
  personalLayers$ = computed(() => {
    const state = this.personalLayersState$()
    return state.status === 'success' ? state.data : []
  })
  sharedLayersState$ = signal<AsyncState<LayerUploaded[]>>(asyncLoading())
  sharedLayers$ = computed(() => {
    const state = this.sharedLayersState$()
    return state.status === 'success' ? state.data : []
  })
  layersLoading$ = signal<Record<number, boolean>>({})

  uploadedLayersFeatureGroup = new L.FeatureGroup() // Used to clear all layer

  selectedLayers$ = computed(() => {
    const personalLayersSelected = this.personalLayers$().filter((layer) => layer.isSelected)
    const uploadedLayersSelected = this.sharedLayers$().filter((layer) => layer.isSelected)
    return [...personalLayersSelected, ...uploadedLayersSelected]
  })

  sortSelectedPersonalLayer$ = signal<string>('oldToNew')
  sortSelectedSharedLayer$ = signal<string>('oldToNew')

  // Computed signal for selected layers IDs
  selectedUploadLayersIds$ = computed(() => {
    const personal = this.personalLayers$()
    const shared = this.sharedLayers$()
    if (!personal || !shared) {
      return []
    }
    const selectedPersonal = personal.filter((layer) => layer.isSelected).map((layer) => layer.id)
    const selectedShared = shared.filter((layer) => layer.isSelected).map((layer) => layer.id)
    return [...selectedPersonal, ...selectedShared]
  })
  selectedUploadLayers$ = computed(() => {
    if (this.personalLayers$() && this.sharedLayers$()) {
      const selectedPersonal = this.personalLayers$().filter((layer) => layer.isSelected)

      const selectedShared = this.sharedLayers$().filter((layer) => layer.isSelected)

      return [...selectedPersonal, ...selectedShared]
    }
  })

  selectedUploadLayersMapped$ = computed(() => {
    if (this.selectedUploadLayers$()) {
      return this.selectedUploadLayers$()?.map((layer) => ({
        id: layer.id,
        user_token: this.keycloak.tokenParsed?.sub || '',
        layer_id: layer.layer || layer.layer_type,
        layer_name: layer.name,
      }))
    }
  })

  filteredPersonalLayers$ = computed(() =>
    this.personalLayers$().filter((layer) =>
      this._searchLayerByName(layer.name, layer.layer ?? '', layer.layer_type),
    ),
  )

  filteredSharedLayers$ = computed(() => {
    const sharedLayers = this.sharedLayers$()
    if (!sharedLayers) return []
    return sharedLayers.filter((layer) =>
      this._searchLayerByName(layer.name, layer.layer ?? '', layer.layer_type),
    )
  })

  stillSpaceOnAccountState$ = signal<AsyncState<boolean>>(asyncLoading())

  constructor() {
    super()
  }

  private _setPersonalLayers(layers: LayerUploaded[]) {
    this.personalLayersState$.set(asyncSuccess(layers))
  }

  private _setSharedLayers(layers: LayerUploaded[]) {
    this.sharedLayersState$.set(asyncSuccess(layers))
  }

  filterPersonalOrShareLayerWithTypeInProject(layerToFilter: LayerUploaded) {
    if (layerToFilter.layer_type == 'Geojson') return true

    const layer = LAYERS_ARRAY.find((layer) => layerToFilter.layer === layer.workspacename)
    if (!layer) return
    if (layer.projects?.includes(environment.appName)) {
      return true
    }
  }

  async addPersonalLayer(
    file: File,
    layer: LayerPersonalPayload,
    sharedWith: String[] = [],
  ): Promise<{ id: number; message: string }[]> {
    const form = new FormData()

    form.append('file', file, file.name)
    form.append('workspacename', layer.workspacename)
    form.append('layer_type', layer.layer_type)
    form.append('shared_with', JSON.stringify(sharedWith))
    const url = this._baseUrl + '/add'
    const result = await this.POST(form, url)

    return [result]
  }

  async updatePersonalLayer(id: number, sharedWith?: String[]): Promise<any> {
    const form = new FormData()
    form.append('id', JSON.stringify(id))
    form.append('shared_with', JSON.stringify(sharedWith))
    const url = this._baseUrl + '/update'
    return await this.POST(form, url)
  }

  async deletePersonalLayer(id: number): Promise<any> {
    const result = await this.DELETE(id)
    await this.list()
    this.sortUploadedLayers('personal', this.sortSelectedPersonalLayer$())
    await this.checkSizeAccount()
    return result
  }

  async deleteSharedLayer(id: number): Promise<any> {
    const result = await this.DELETE(id, this._baseUrl + '/shared/' + id)
    await this.listShare()
    this.sortUploadedLayers('shared', this.sortSelectedSharedLayer$())
    return result
  }

  getUserPersonalAndSharedLayers() {
    this.list()
    this.listShare()
  }

  async list() {
    const previousLayersById = new Map(this.personalLayers$().map((layer) => [layer.id, layer]))

    this.personalLayersState$.set(asyncLoading())

    const url = this._baseUrl + '/list'
    const response = await this.GETlist(url)

    if (response === null) {
      this.personalLayersState$.set(asyncError())
      return
    }

    //@TODO: remove the key "uploads" in backend and remove sorting
    const resultSorted = response.uploads.sort((a: LayerUploaded, b: LayerUploaded) => a.id - b.id)

    const resultFiltered = resultSorted
      .filter((file: LayerUploaded) => this.filterPersonalOrShareLayerWithTypeInProject(file))
      .map((file: LayerUploaded) => {
        const previousLayer = previousLayersById.get(file.id)

        return {
          ...file,
          isSelected: previousLayer?.isSelected ?? false,
          sizeState: previousLayer?.sizeState,
          leafletReference: previousLayer?.leafletReference,
        }
      })

    this._setPersonalLayers(resultFiltered)
  }

  async listShare() {
    this.sharedLayersState$.set(asyncLoading())
    const url = this._baseUrl + '/listshare'
    const response = await this.GETlist(url)

    if (response === null) {
      this.sharedLayersState$.set(asyncError())
      return
    }

    //@TODO: remove the key "uploads" in backend and remove sorting
    const resultSorted = response.uploads.sort((A: LayerUploaded, B: LayerUploaded) => A.id - B.id)

    const resultFiltered = resultSorted
      .filter((file: LayerUploaded) => this.filterPersonalOrShareLayerWithTypeInProject(file))
      .map((file: LayerUploaded) => {
        // To keep selected layers as selected
        this.sharedLayers$().forEach((layer: LayerUploaded) => {
          if (layer.id === file.id) {
            file.sizeState = layer.sizeState
            file.isSelected = layer.isSelected
            file.leafletReference = layer.leafletReference
          }
        })
        return { ...file }
      })

    this._setSharedLayers(resultFiltered)
  }

  downloadPersonalLayer(id: number): Promise<Blob> {
    const url = this._baseUrl + '/download/' + id
    return this.POSTtoGetBlob(
      {
        id: id,
      },
      url,
    )
  }

  removeUploadedLayerFromMap(uploadedLayer: LayerUploaded): void {
    if (uploadedLayer.leafletReference) {
      // can happen in localhost mode
      this.uploadedLayersFeatureGroup.removeLayer(uploadedLayer.leafletReference)
    }
  }

  private _setLayerLoadingStatus(layerId: number, isLoading: boolean) {
    this.layersLoading$.update((layers) => ({
      ...layers,
      [layerId]: isLoading,
    }))
  }

  private async _showUploadedLayer(id: number | LayerUploaded) {
    const upFile: LayerUploaded =
      typeof id === 'number'
        ? this.personalLayers$().filter((upload) => upload.id == id)[0]
        : (id as LayerUploaded)

    let layerToAdd: L.Layer | undefined

    if (upFile.name.endsWith('.tif')) {
      // Here, we manually set the token to the request as it is a Get and does not allow custom header variables
      const token = this.keycloak.token // Get the Keycloak token
      //Retrieve max zoom level
      const url = this._baseUrl + '/maxzoom/' + upFile.id
      const response = await this.GETobject(upFile.id, url)
      if (response === null) return null

      const maxNativeZoom = response?.maxZoom || 11 // Fallback to 11 if no maxZoom is provided
      layerToAdd = await L.tileLayer(
        `${this._baseUrl}/tiles/{id}/{z}/{x}/{y}?token=${token}`, // Attach token as URL parameter
        {
          id: upFile.id.toString(),
          tms: true,
          maxNativeZoom: maxNativeZoom,
          zIndex: PERSONAL_LAYERS_ORDER, // Prevents layer from being hidden by map
        },
      )
    }

    if (upFile.name.endsWith('.csv')) {
      const url = this._baseUrl + '/csv/' + upFile.id
      const response = await this.GETobject(upFile.id, url)
      if (response === null) return null
      const matchedLayer = LAYERS_ARRAY.find(
        (layer) => layer.layer_type?.includes(upFile.layer_type) && layer.id == 0,
      )

      if (matchedLayer) {
        const xmlSld = await this._sldService.getStyleFromGeoserver(
          upFile.layer || upFile.layer_type,
        )
        if (xmlSld === null) {
          console.error('Failed to load SLD file, display geojson with default option')
          layerToAdd = this._geoJsonService.buildGeoJSONLayerGroup(response)
        } else {
          layerToAdd = await this._geoJsonService.buildStylizedGeoJsonLayerGroup(response, xmlSld)
        }
      } else {
        layerToAdd = this._geoJsonService.buildGeoJSONLayerGroup(response)
      }
    }

    if (upFile.name.endsWith('.geojson')) {
      const url = this._baseUrl + '/geojson/' + upFile.id
      const response = await this.GETobject(upFile.id, url)
      if (response === null) return null

      const matchedLayer = LAYERS_ARRAY.find(
        (layer) => layer.layer_type?.includes(upFile.layer_type) && layer.id == 0,
      )

      if (matchedLayer) {
        const xmlSld = await this._sldService.getStyleFromGeoserver(upFile.layer_type)
        if (xmlSld === null) {
          console.error('Failed to load SLD file, display geojson with default option')
          layerToAdd = this._geoJsonService.buildGeoJSONLayerGroup(response)
        } else {
          layerToAdd = await this._geoJsonService.buildStylizedGeoJsonLayerGroup(response, xmlSld)
        }
      } else {
        layerToAdd = this._geoJsonService.buildGeoJSONLayerGroup(response)
      }
    }

    if (layerToAdd) {
      this.uploadedLayersFeatureGroup.addLayer(layerToAdd)
      return layerToAdd
    }
  }
  async getLayerSize(id: number) {
    return await this.GET(`${this._baseUrl}/${id}/size`)
  }

  getSizeWithUnit(size: number) {
    if (size == 0) {
      return null
    } else if (size >= 1024 * 1024 * 1024) {
      return (size / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
    } else if (size >= 1024 * 1024) {
      return (size / (1024 * 1024)).toFixed(1) + ' MB'
    } else if (size >= 1024) {
      return (size / 1024).toFixed(1) + ' KB'
    } else {
      return size.toFixed(1) + ' B'
    }
  }

  private _updateUploadedLayer(
    source: 'personal' | 'shared',
    layerId: number,
    patch: Partial<LayerUploaded>,
  ) {
    if (source === 'personal') {
      this._setPersonalLayers(
        this.personalLayers$().map((l) => (l.id === layerId ? { ...l, ...patch } : l)),
      )
    } else {
      this._setSharedLayers(
        this.sharedLayers$().map((l) => (l.id === layerId ? { ...l, ...patch } : l)),
      )
    }
  }

  async actionLayer(source: 'personal' | 'shared', layer: LayerUploaded) {
    if (layer.isSelected) {
      this.removeUploadedLayerFromMap(layer)
      this._updateUploadedLayer(source, layer.id, { isSelected: false, displayError: false })
    } else {
      this._setLayerLoadingStatus(layer.id, true)
      this._updateUploadedLayer(source, layer.id, { displayError: false })

      this._showUploadedLayer(layer)
        .then((reference) => {
          this._updateUploadedLayer(source, layer.id, {
            isSelected: true,
            displayError: false,
            sizeState: asyncLoading(),
            leafletReference: reference,
          })
          this._setLayerLoadingStatus(layer.id, false)

          // Set size
          this.getLayerSize(layer.id)
            .then((size) => {
              const sizeWithUnit = this.getSizeWithUnit(size)
              const sizeState =
                sizeWithUnit === null ? asyncError<string>() : asyncSuccess(sizeWithUnit)
              this._updateUploadedLayer(source, layer.id, { sizeState })
            })
            .catch((error) => {
              console.error(error)
              this._updateUploadedLayer(source, layer.id, { sizeState: asyncError<string>() })
            })
        })
        .catch((error) => {
          console.error(error)
          this._updateUploadedLayer(source, layer.id, { isSelected: false, displayError: true })
          this._setLayerLoadingStatus(layer.id, false)
        })
    }
  }

  unCheckAllUploadedLayers() {
    this.uncheckAllPersonalLayers()
    this.uncheckAllSharedLayers()
  }

  uncheckAllPersonalLayers() {
    this.personalLayers$().forEach((layer) => {
      if (layer.isSelected) {
        this.actionLayer('personal', layer)
      }
    })
  }

  uncheckAllSharedLayers() {
    this.sharedLayers$().forEach((layer) => {
      if (layer.isSelected) {
        this.actionLayer('shared', layer)
      }
    })
  }

  async checkSizeAccount() {
    this.stillSpaceOnAccountState$.set(asyncLoading())
    const response = await this.GET(API_URL + '/users/space-used')
    if (response === null) {
      this.stillSpaceOnAccountState$.set(asyncError())
      return
    }
    if (typeof response.used_size !== 'undefined') {
      this.stillSpaceOnAccountState$.set(asyncSuccess(response.used_size < response.max_size))
    }
  }

  private _layerComparator(order: string): (a: LayerUploaded, b: LayerUploaded) => number {
    if (order === 'name') {
      return (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
    }
    if (order === 'newToOld') {
      return (a, b) => (b.id ?? 0) - (a.id ?? 0)
    }
    // Default oldToNew (chronological order)
    return (a, b) => (a.id ?? 0) - (b.id ?? 0)
  }

  sortUploadedLayers(source: 'personal' | 'shared', order: string) {
    const comparator = this._layerComparator(order)
    if (source === 'personal') {
      this.sortSelectedPersonalLayer$.set(order)
      this._setPersonalLayers([...this.personalLayers$()].sort(comparator))
    } else if (source === 'shared') {
      this.sortSelectedSharedLayer$.set(order)
      this._setSharedLayers([...this.sharedLayers$()].sort(comparator))
    }
  }

  async getCSVSymbology(layer: LayerUploaded) {
    const url = this._baseUrl + '/csv/' + layer.id
    const response = await this.GETobject(layer.id, url)
    if (response === null) return null

    return response.symbology
  }

  async uploadedLayerCenterMap(upFile: LayerUploaded) {
    if (!upFile?.name) return null
    let bounds

    if (upFile.name.endsWith('.geojson') || upFile.name.endsWith('.csv')) {
      // Fetch the geojson or json
      const type = upFile.name.endsWith('.geojson') ? 'geojson' : 'csv'
      const url = `${this._baseUrl}/${type}/${upFile.id}`
      const response = await this.GETobject(upFile.id, url)

      // Find bbox from fetched data
      const [minLng, minLat, maxLng, maxLat] = bbox(response)
      bounds = L.latLngBounds([minLat, minLng], [maxLat, maxLng])
    } else if (upFile.name.endsWith('.tif')) {
      const url = `${this._baseUrl}/raster-bounds/${upFile.id}`
      const response = await this.GETobject(upFile.id, url)
      bounds = L.latLngBounds(
        [response.minLat, response.minLng],
        [response.maxLat, response.maxLng],
      )
    }

    if (bounds) {
      this._map.getValue().fitBounds(bounds)
    }
  }

  private _searchLayerByName(name: string, layerName: string, layerType: string): boolean {
    const searchTerm = this._layerService.search$().toLowerCase()
    return (
      name.toLowerCase().includes(searchTerm) ||
      !!(layerName && layerName.toLowerCase().includes(searchTerm)) ||
      !!(layerType && layerType.toLowerCase().includes(searchTerm))
    )
  }
}
